/* eslint-disable @atlassian/relay/unused-fields */

import React, {
	type ComponentPropsWithoutRef,
	forwardRef,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { TEAM_MANAGED_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { isProjectsInJiraM2Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { isNormalClick } from '@jira/navigation-apps__sidebar-common/src/utils/is-normal-click.tsx';
import { useProjectsAnalyticsL1 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useGetLinkProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-link-props/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { removeBaseUrl } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/build-project-url/index.tsx';
import { RouteEntryPointContainer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/route-entry-point-adapter/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import type { LeftSidebarEntryPointConfig } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { ProjectActions } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-common/src/ui/project-actions/ProjectActions.tsx';
import { useProjectMenuItemDragAndDrop } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { getProjectKeyFromProjectOrIssueKey } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { ContainerAvatar as Avatar } from '@atlaskit/side-nav-items/container-avatar';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
	useIsExpanded,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { useWithSetWillRedirectOnLoad } from '@atlassian/redirect-on-load/use-with-set-will-redirect-on-load';
import { useSetWillRedirectOnLoad } from '@atlassian/redirect-on-load/use-set-will-redirect-on-load';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type { JswExpandableProject$key } from './__generated__/JswExpandableProject.graphql';
import { nav4JSWProjectEntryPoint } from './async-content/nav4JSWProjectEntryPoint.entrypoint.tsx';

type JswExpandableProjectProps = {
	projectData: JswExpandableProject$key;
	menuItemButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const JswExpandableProject = ({
	projectData,
	menuItemButtonRef,
}: JswExpandableProjectProps): React.JSX.Element | null => {
	const { sendProjectMenuItemAnalyticsEvent } = useProjectsAnalyticsL1();
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<JswExpandableProject$key>(
		graphql`
			fragment JswExpandableProject on JiraProject
			@argumentDefinitions(
				isSubspaceEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/atlas-project-native-integration-m2.relayprovider"
				}
			) {
				id
				projectKey: key @required(action: THROW)
				projectStyle
				projectId @required(action: THROW)
				isFavourite
				projectType
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				projectKey: key @required(action: THROW)
				webUrl @required(action: THROW)
				defaultNavigationItem @include(if: $isSubspaceEnabled) {
					... on JiraNavigationItem {
						url
					}
				}
				...ProjectActions
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		projectData,
	);
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();

	// CRITICAL: This experiment evaluation must match the one in entrypoint.tsx
	// to ensure consistent behavior between component parameters and dynamic imports
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const entryPointParams = useMemo(
		() => ({
			tenantContext: { cloudId, atlassianAccountId: accountId || '', activationId },
			context: {
				match: {
					params: {
						projectKey: project.projectKey,
						projectStyle: project.projectStyle,
					},
				},
			},
		}),
		[accountId, activationId, cloudId, project.projectKey, project.projectStyle],
	) as unknown as EntryPointRouteParams;

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const boardsEPRef = leftSidebarEPRef?.entryPoints?.boards;

	const [projectKeyPathParam] = usePathParam('projectKey');
	const [issueKeyPathParam] = usePathParam('issueKey');

	let projectKey = projectKeyPathParam;
	if (issueKeyPathParam && fg('move-issue-view-to-entrypoint')) {
		projectKey = getProjectKeyFromProjectOrIssueKey('', issueKeyPathParam);
	}

	const shouldRenderBoardsEntryPoint = project.projectKey === projectKey;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		nav4JSWProjectEntryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);

	const { getLinkProps } = useGetLinkProps();

	// Create this component once, then keep using it FOREVER.
	// We don't want to keep switching the implementation as the user does SPA transitions.
	const [entryPointConfig] = useState(() => {
		// For the refactored experiment, we need dynamic props to ensure consistency
		return {
			Component: () => {
				// Generate props dynamically each time to ensure experiment consistency
				const epContainerProps = {
					id: 'jsw-expandable-project',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
					errorFallback: 'flag' as const,
					fallback: <SkeletonNavMenuItems />,
					// oxlint-disable-next-line typescript/consistent-type-assertions
					runtimeProps: entryPointParams as never,
				};
				return (
					<RouteEntryPointContainer
						epContainerProps={epContainerProps}
						jiraEPRef={entryPointReferenceSubject}
						nestedEPRef={boardsEPRef}
						placeholderEntryPoint={nav4JSWProjectEntryPoint}
						shouldRenderNestedEntryPoint={shouldRenderBoardsEntryPoint}
					/>
				);
			},
		};
	});

	const setWillRedirectOnLoad = useSetWillRedirectOnLoad();

	const onSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			} else {
				setWillRedirectOnLoad(false);
			}
		},
		[entryPointActions, setWillRedirectOnLoad],
	);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();

				sendProjectMenuItemAnalyticsEvent({
					starred: project.isFavourite ?? false,
					recent: !project.isFavourite,
					projectId: project.projectId,
					projectType: project.projectType,
				});
			} else {
				setWillRedirectOnLoad(false);
			}
		},
		[
			entryPointActions,
			project.isFavourite,
			project.projectId,
			project.projectType,
			sendProjectMenuItemAnalyticsEvent,
			setWillRedirectOnLoad,
		],
	);

	const onClick = useCallback(
		(ev: React.MouseEvent) => {
			if (isNormalClick(ev)) {
				if (project.projectStyle === TEAM_MANAGED_PROJECT && isProjectsInJiraM2Enabled()) {
					// for tmp project, click on the space name will navigate to the space page
					return;
				}
				ev.preventDefault();
			}
		},
		[project.projectStyle],
	);

	const { state, draggableAnchorRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId: project.projectId,
			projectKey: project.projectKey,
			isFavourite: project.isFavourite,
			name: project.name,
			avatar: project.avatar,
			isEnabled: !!project.isFavourite,
		});

	if (!project) {
		return null;
	}

	// oxlint-disable-next-line react/react-compiler
	const menuId = ENTITY_ID.PROJECT(project.projectKey);

	return (
		<>
			<Nav4ExpandableMenuItem
				menuId={menuId}
				onSystemToggle={onSystemToggle}
				onExpansionToggle={onExpansionToggle}
				dropIndicator={dropIndicator}
				dropTargetRef={dropTargetRef}
				isDragging={state.type === 'dragging'}
			>
				<ExpandableMenuItemTriggerWithSetWillRedirectOnLoad
					{...getLinkProps(
						isProjectsInJiraM2Enabled() && project.projectStyle === TEAM_MANAGED_PROJECT
							? (project.defaultNavigationItem?.url ?? removeBaseUrl(project.webUrl))
							: removeBaseUrl(project.webUrl),
					)}
					actionsOnHover={<ProjectActions projectFragment={project} />}
					elemBefore={<Avatar src={project.avatar.large} />}
					ref={mergeRefs(draggableAnchorRef, ...[triggerRef, menuItemButtonRef].filter(Boolean))}
					onClick={onClick}
					testId={getTestId(menuId)}
					hasDragIndicator={hasDragIndicator}
					isDragging={state.type === 'dragging'}
					interactionName="nav4-sidebar-jsw-project"
				>
					{project.name}
				</ExpandableMenuItemTriggerWithSetWillRedirectOnLoad>
				<ExpandableMenuItemContent>
					<entryPointConfig.Component />
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{dragPreview}
		</>
	);
};

type ExpandableMenuItemTriggerProps = ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>;

// A thin wrapper so we can access `isExpanded` via hook.
// We redirect on load when the label is clicked, so we only do this in `onClick` and not in any of the toggle handlers (which the chevron also uses).
const ExpandableMenuItemTriggerWithSetWillRedirectOnLoad = forwardRef<
	HTMLButtonElement | HTMLAnchorElement,
	ExpandableMenuItemTriggerProps
>(({ children, onClick, ...props }, ref) => {
	const isExpanded = useIsExpanded();

	// If not currently expanded we want to redirect on load.
	// If currently expanded, the toggle handlers above will set to false.
	const onClickActual = useWithSetWillRedirectOnLoad(onClick, true, !isExpanded);

	return (
		<ExpandableMenuItemTrigger onClick={onClickActual} ref={ref} {...props}>
			{children}
		</ExpandableMenuItemTrigger>
	);
});
